import { isNull } from 'util';

import classNames from 'classnames';
import { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { InfoBox } from 'shared/ui/InfoBox';
import type { TableProps } from 'shared/ui/Table/table-models';
import { noop } from 'shared/lib/functions';
import { HEAD_LAYOUT_ID } from 'entities/faq';

import style from './Table.module.scss';
import { renderRows, renderLoadingRows, setSticky, getFilteredChildren, useActiveRow, getRect } from './TableUtils';

export function Table<T>({
  data = [],
  isLoading = false,
  isDisabled = false,
  columns,
  onRowClick,
  rowClassName,
  rowKey,
  className,
  variant = 'table',
  hasRowCursorPointer,
  hasVerticalScroll,
  skeletonColumnsCount,
  isStickyToTopPage = false,
  offsetElement,
  modalContainer = null,
  isAlwaysSticky,
}: TableProps<T>) {
  const [isSticky, setIsSticky] = useState(false);
  const listenerFunction = useRef<() => void>(noop);
  const getActiveRow = useActiveRow();

  const ref = useCallback(
    (node: HTMLTableSectionElement | null) => {
      if (!isNull(node)) {
        const fn = () => {
          const layoutHeaderRect = getRect(offsetElement ?? document.querySelector(`#${HEAD_LAYOUT_ID}`));
          const modalContainerRect = getRect(modalContainer);

          let tableHeaderOffset = layoutHeaderRect.top + layoutHeaderRect.height - modalContainerRect.top;

          if (!offsetElement) {
            tableHeaderOffset -= 1;
          }

          const offsetLeft = modalContainer ? layoutHeaderRect.left - modalContainerRect.left : undefined;

          setSticky({
            node,
            offsetTop: tableHeaderOffset,
            offsetLeft,
            setIsSticky,
            isAlwaysSticky,
          });
        };

        listenerFunction.current = fn;

        document.addEventListener('scroll', fn, true);
        window.addEventListener('resize', fn, true);
      }
    },
    [offsetElement, modalContainer, isAlwaysSticky]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener('scroll', listenerFunction.current, true);
      window.removeEventListener('resize', listenerFunction.current, true);
    };
  }, []);

  const tableClass = classNames(className, style[variant], {
    [style.tableVerticalScroll]: hasVerticalScroll,
    [style.rowHoverPointer]: hasRowCursorPointer,
    [style.disabledTable]: isDisabled,
  });
  const headerCells = columns.map(({ key, label, headerCell }) => cloneElement(headerCell({ value: label }), { key }));

  const stickyHeaderCells = columns.map(({ key, label, headerCell }) => {
    const child = headerCell({ value: label });

    const filteredChild = getFilteredChildren(child);

    return cloneElement(filteredChild, {
      key,
      className: classNames(filteredChild.props.className, style.stickyHeader),
    });
  });

  const rows =
    isLoading && !data.some(({ loading }) => loading)
      ? renderLoadingRows(columns, skeletonColumnsCount)
      : renderRows({ data, rowClassName, rowKey, onRowClick, columns, getActiveRow });

  if (!isLoading && !data.length) {
    return (
      <InfoBox
        title="🧐 No results"
        text="We couldn’t find what you were looking for. Check your request and search again."
        className="mt-6"
      />
    );
  }

  const showStickyHeader = isStickyToTopPage && isSticky && variant === 'table';

  return (
    <div
      className={tableClass}
      ref={ref}
    >
      {isStickyToTopPage === true ? (
        <table>
          <thead
            className={classNames(style.stickyHeaderWrapper, isAlwaysSticky)}
            style={{
              visibility: showStickyHeader ? 'unset' : 'hidden',
            }}
          >
            <tr className={style.stickyHeaderBoarder}>{stickyHeaderCells}</tr>
          </thead>
        </table>
      ) : null}
      <table>
        <thead className="uppercase">
          <tr>{headerCells}</tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}
